import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import {
  BookingWidgetStore,
  STEP_HEADERS,
} from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/store/bookingWidgetStore'
import { BookingStep1 } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/Steps/BookingStep1'
import { BookingStep2 } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/Steps/BookingStep2'
import { BookingStep3 } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/Steps/BookingStep3'
import { BookingStep4 } from 'pages/chatbot/ui/UnifiedAgent/ui/BookingSection/ui/Steps/BookingStep4'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'

const DEFAULT_HINT = ['↑↓ to navigate', 'Enter to select']

export const BookingWidget = observer(() => {
  const { bookingSectionStore, chatStore, instructionsSectionStore } = useUnifiedAgentContext()
  const [store] = useState(
    () => new BookingWidgetStore(bookingSectionStore, chatStore, instructionsSectionStore)
  )

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  useWidgetKeyboard({
    onNavigate: (dir) => store.moveFocus(dir),
    onEnter: store.handleSelect,
    onSubmit: store.handleSubmit,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const hintItems = store.isLastStep
    ? getLastStepHintMessage(['↑↓ to navigate'])
    : [...DEFAULT_HINT, 'Esc to skip']

  const renderContent = () => {
    switch (store.step) {
      case 1:
        return (
          <BookingStep1
            items={bookingSectionStore.connectionList}
            focusedIndex={store.safeIndex}
            isLoading={store.isSelectingIntegration || bookingSectionStore.isLoading}
            onSelect={store.selectAt}
          />
        )
      case 2:
        return (
          <BookingStep2
            items={bookingSectionStore.meetingsList}
            focusedIndex={store.safeIndex}
            isLoading={bookingSectionStore.isMeetingLoading}
            search={store.search}
            onSearch={store.setSearch}
            onSelect={store.selectAt}
          />
        )
      case 3:
        return (
          <BookingStep3
            focusedIndex={store.safeIndex}
            onSelect={store.selectAt}
            isHubspotSelected={store.isLocalHubspotSelected}
          />
        )
      case 4:
        return <BookingStep4 focusedIndex={store.safeIndex} onSelect={store.setFocusedIndex} />
      default:
        return null
    }
  }

  return (
    <>
      <WidgetCard header={STEP_HEADERS[store.step - 1]}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            store.isLastStep && (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
              />
            )
          }
        />
      </WidgetCard>
      <WidgetHint items={hintItems} />
    </>
  )
})
